/**
 * The programmes list: what an account can reach, and the way into each.
 *
 * @module router/programmes/ProgrammesRoute
 */

import React, { useState } from "react";

import { useNavigate } from "react-router";

import { AssignedModuleList } from "@/components/programmes/AssignedModuleList";
import { programmeEditRefusal } from "@/components/programmes/programme-edit-refusal";
import { ProgrammeList } from "@/components/programmes/ProgrammeList";
import { programmeFilterDefs } from "@/components/shared/programme-filter-defs";
import {
  Alert,
  Heading,
  PageHeader,
  Panel,
  type SortState,
  useCollectionFilters,
} from "@/components/ui";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/**
 * A builder's own modules sit above the programmes they belong to, because the
 * module is the unit of their work and the programme is only its address.
 */
export function ProgrammesRoute(): React.ReactElement {
  const navigate = useNavigate();
  const {
    profile,
    session,
    platformProgrammes,
    platformModules,
    platformDataError,
    setProgrammeViewMode,
  } = useWorkspaceOutletContext();
  const open = (programmeId: string, viewing: boolean): void => {
    setProgrammeViewMode(programmeId, viewing);
    navigate(`/programmes/${programmeId}/overview/identity`);
  };

  // The screen names what it filters on; the panel, the result count and the
  // clear control come back built.
  const { rows, filtering } = useCollectionFilters(
    platformProgrammes,
    programmeFilterDefs({
      workflow: "programme-filter-select",
      level: "programme-filter-level-select",
      discipline: "programme-filter-discipline-select",
      text: "programme-filter-text-input",
    }),
    { label: "Filter programmes", noun: "programme", "data-testid": "programme-filter-bar" },
  );
  const [sort, setSort] = useState<SortState>({ key: "title", direction: "asc" });

  const isBuilder = profile.role === "builder";

  return (
    <div className="flex flex-col gap-6" data-testid="programmes-route">
      <PageHeader
        title="Programmes"
        description={
          isBuilder
            ? "Modules assigned to you and the programmes they belong to."
            : "Programmes you have access to and what each needs next."
        }
      />

      {platformDataError ? <Alert tone="warning">{platformDataError.message}</Alert> : null}

      {/* Nothing at all once the load failed. An empty state is a claim about
          the tenant, and "No programmes yet" told someone whose network had
          dropped that they had none. The warning above is the whole answer. */}
      {!platformDataError && (
        <>
          {isBuilder && (
            <Panel data-testid="builder-modules">
              <Heading level={2} size="md">
                Your modules
              </Heading>
              <div className="mt-4">
                <AssignedModuleList
                  modules={platformModules}
                  onOpen={(programmeId, moduleId) => {
                    setProgrammeViewMode(programmeId, false);
                    navigate(`/programmes/${programmeId}/modules/${moduleId}`);
                  }}
                />
              </div>
            </Panel>
          )}

          <ProgrammeList
            programmes={rows}
            filtering={filtering}
            sort={sort}
            onSortChange={setSort}
            onOpen={(programmeId) => {
              open(programmeId, false);
            }}
            onView={(programmeId) => {
              open(programmeId, true);
            }}
            onEdit={(programmeId) => {
              open(programmeId, false);
            }}
            editRefusal={(programme) => programmeEditRefusal(session, programme)}
            onOpenTeam={(programmeId) => {
              navigate(`/programmes/${programmeId}/team`);
            }}
            onOpenModules={(programmeId) => {
              setProgrammeViewMode(programmeId, false);
              navigate(`/programmes/${programmeId}/overview/modules`);
            }}
          />
        </>
      )}
    </div>
  );
}
